import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, first, map, Observable, switchMap, take } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { BaseComponent } from '../../base/base.component';
import { createExoSafeAttachmentPolicySuccess } from './../../../../../../../stores/client/powershell/exo/safe-attachment-policy/actions';
import { buildSafeAttachmentPolicySchema } from 'projects/angular-clarity/src/app/modules/sway/models/baseline-schemas/safe-attachment-policy.schema';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { SafeAttachmentPolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/safe-attachment-policy/model';
import { QuarantinePolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/quarantine-policy/model';
import { selectSession } from 'projects/angular-clarity/src/app/stores/root.store';
import { isValidEmail } from 'projects/angular-clarity/src/app/utils/custom-validators';
import { updateBaseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/actions';
import { Baseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/model';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit, OnChanges {
    policy: SafeAttachmentPolicy;

    users$: Observable<Array<{ id: string; name: string }>>;
    groups$: Observable<Array<{ id: string; name: string }>>;
    domains$: Observable<Array<{ id: string; name: string }>>;
    quarantinePolicies$: Observable<QuarantinePolicy[]>;

    sentToErrors: string[] = [];
    exceptIfSentToErrors: string[] = [];

    constructor(
        private fb: FormBuilder,
        private store: Store<any>,
        private action: Actions,
    ) {
        super();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.policy = this.data?.[0];
    }

    ngOnInit(): void {
        this.policy = this.data?.[0];
        this.observeCreationOffPolicy();
        const schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = this.initForm(schema);

        const hasErrors = this.form.invalid;

        this.baselineErrorChange.next({ remediate: hasErrors, save: hasErrors });

        this.users$ = this.store
            .select(client(this.tenant_id).graph.users.withMailbox)
            .pipe(map((users) => users.map((u) => ({ id: u['id'], name: u['mail'] }))));

        this.groups$ = this.store.select(client(this.tenant_id).graph.groups.all).pipe(
            map((res) =>
                res.filter((g) => g.mailEnabled).map((g) => ({ id: g.id, name: g.displayName, mail: g.mail })),
            ), // Only include mail-enabled groups
        );

        this.domains$ = this.store
            .select(client(this.tenant_id).graph.domains.verified)
            .pipe(map((domains) => domains.map((d) => ({ id: d.id, name: d.id }))));

        this.quarantinePolicies$ = this.getQuarantinePolicy();

        if (this.form.get('Redirect').value) {
            this.form.get('RedirectAddress').addValidators(Validators.required);
            this.form.get('RedirectAddress').updateValueAndValidity();
        }

        this.observeFormChanges();

        this.applyDefaultIncludeTarget();
    }

    /**
     * Ensures the policy has at least one Include target so the baseline can be
     * saved and remediated.
     *
     * The templated baseline seeds every recipient condition as `null`, but the
     * form requires (and Exchange's `New-SafeAttachmentRule` mandates) at least
     * one condition — otherwise the form stays invalid and both the Save and
     * Remediate buttons are permanently disabled (OS-433). When no target is
     * configured we default `RecipientDomainIs` to the tenant's verified domains
     * so the policy applies organization-wide. The value is written into the
     * schema on Save/Remediate, keeping the created rule and the deviation check
     * consistent.
     */
    private applyDefaultIncludeTarget(): void {
        const value = this.formValue;
        const hasIncludeTarget =
            (value.SentTo?.length ?? 0) > 0 ||
            (value.SentToMemberOf?.length ?? 0) > 0 ||
            (value.RecipientDomainIs?.length ?? 0) > 0;

        if (hasIncludeTarget) {
            return;
        }

        const sub = this.domains$
            .pipe(
                filter((domains) => domains.length > 0),
                take(1),
            )
            .subscribe((domains) => {
                this.form.get('RecipientDomainIs').patchValue(
                    domains.map((d) => d.name),
                    { emitEvent: true },
                );
            });

        this.subs.push(sub);
    }

    observeCreationOffPolicy() {
        const sub = this.action.pipe(ofType(createExoSafeAttachmentPolicySuccess)).subscribe((res) => {
            if (res) {
                const policyId = res.data.Identity;

                const data: Baseline = { ...this.baseline, key: policyId };

                const _tenant = this.tenant_id;

                this.store.dispatch(updateBaseline({ _tenant, data }));
            }
        });

        this.subs.push(sub);
    }

    initForm(schema: any): FormGroup {
        return this.fb.group(
            {
                Identity: [schema?.contains?.properties?.Identity?.const, Validators.required],
                Priority: [
                    schema?.contains?.properties?.Priority?.const ?? 0,
                    [Validators.required, Validators.min(0)],
                ],
                Enable: [schema?.contains?.properties?.Enable?.const],
                Action: [schema?.contains?.properties?.Action?.const ?? 'Block'],
                QuarantineTag: [schema?.contains?.properties?.QuarantineTag?.const ?? 'AdminOnlyAccessPolicy'],
                Redirect: [schema?.contains?.properties?.Redirect?.const ?? false],
                RedirectAddress: [schema?.contains?.properties?.RedirectAddress?.const ?? '', Validators.email],
                SentTo: [schema?.contains?.properties?.SentTo?.items?.enum ?? []],
                SentToMemberOf: [schema?.contains?.properties?.SentToMemberOf?.items?.enum ?? []],
                RecipientDomainIs: [schema?.contains?.properties?.RecipientDomainIs?.items?.enum ?? []],
                ExceptIfSentTo: [schema?.contains?.properties?.ExceptIfSentTo?.items?.enum ?? []],
                ExceptIfSentToMemberOf: [schema?.contains?.properties?.ExceptIfSentToMemberOf?.items?.enum ?? []],
                ExceptIfRecipientDomainIs: [schema?.contains?.properties?.ExceptIfRecipientDomainIs?.items?.enum ?? []],
            },
            { validators: requireOneIncludeTarget },
        );
    }

    observeFormChanges(): void {
        this.form.get('Redirect').valueChanges.subscribe((redirect: boolean) => {
            const redirectAddress = this.form.get('RedirectAddress');
            if (redirect) {
                redirectAddress.addValidators(Validators.required);
            } else {
                redirectAddress.removeValidators(Validators.required);
            }
            redirectAddress.updateValueAndValidity();
        });

        this.form.valueChanges.subscribe(() => {
            const value = this.form.getRawValue();

            this.sentToErrors = (value.SentTo || []).filter((item) => !isValidEmail(item));
            this.exceptIfSentToErrors = (value.ExceptIfSentTo || []).filter((item) => !isValidEmail(item));

            const hasErrors = this.form.invalid;

            this.baselineErrorChange.next({ remediate: hasErrors, save: hasErrors });

            const updatedSchema = buildSafeAttachmentPolicySchema(value);
            this.baselineChange.next({
                ...this.baseline,
                schema: updatedSchema,
            });
        });
    }

    updateField(field: string, value: any): void {
        this.form.get(field).patchValue(value);
    }

    getQuarantinePolicy(): Observable<QuarantinePolicy[]> {
        return this.store.pipe(
            select(selectSession),
            map((sess) => sess.session.clientId),
            switchMap((tid) =>
                combineLatest([
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.all),
                    this.store.select(client(tid).powershell.exoQuarantinePolicy.status),
                ]),
            ),
            first(([, status]) => status.loaded),
            map(([data]) => data),
        );
    }

    get formValue() {
        return this.form.getRawValue();
    }

    readonly actionLabels: Record<string, string> = {
        Allow: 'Off - Attachments will not be scanned by Safe Attachments.',
        Block: 'Block - Block current and future messages and attachments with detected malware.',
        DynamicDelivery:
            'Dynamic Delivery (Preview messages) - Immediately deliver the message without attachments. Reattach files after scanning is complete.',
    };

    get redirectEnabled(): boolean {
        return this.formValue.Redirect === true;
    }

    get includeTargetError(): boolean {
        return this.form?.hasError('requireOneIncludeTarget') && this.form?.touched;
    }

    get excludeError(): boolean {
        return this.exceptIfSentToErrors?.length > 0;
    }

    get settingsError(): boolean {
        return (
            this.form?.get('Identity')?.invalid ||
            this.form?.get('Priority')?.invalid ||
            (this.redirectEnabled && this.form?.get('RedirectAddress')?.invalid)
        );
    }
}

function requireOneIncludeTarget(control: AbstractControl): ValidationErrors | null {
    const sentTo: string[] = control.get('SentTo')?.value ?? [];
    const sentToMemberOf: string[] = control.get('SentToMemberOf')?.value ?? [];
    const recipientDomainIs: string[] = control.get('RecipientDomainIs')?.value ?? [];
    const hasInclude = sentTo.length > 0 || sentToMemberOf.length > 0 || recipientDomainIs.length > 0;
    return hasInclude ? null : { requireOneIncludeTarget: true };
}
